import React, { useMemo } from 'react';
import { useTheme } from 'styled-components';
import {
  LabelMotion,
  SwitcherMotion,
  SwitcherWrapper,
  ToggleWrapper,
  Track,
  TrackMotion,
} from './Toggle.styles';
import { ToggleStateVariant, ToggleVariant } from './types';
import { KEYBOARD_KEYS } from '../../../constants';
import { Theme } from '../../../themeBase';

const switcherTransition = {
  ease: [0.69, -0.55, 0.27, 1.55],
  duration: 0.3,
};
const trackVariants = {
  on: { scaleX: 1 },
  off: { scaleX: 0 },
};
const labelTransition = {
  type: 'tween',
  duration: 0.3,
};

const labelVariants = {
  on: {
    justifyContent: 'flex-start',
  },
  off: {
    justifyContent: 'flex-end',
  },
};

/**
- The Toggle component is used to toggle between two states.
 **/
export type ToggleProps = {
  isOn?: boolean;
  isDisabled?: boolean;
  variant?: ToggleVariant;
  onChange?: (res: boolean) => void;
  size?: 'medium';
  text?: {
    on: string;
    off: string;
  };
};
const Toggle = ({
  /**
   * Is toggle disabled
   */
  isDisabled,
  /**
   * Is toggle default on
   */
  isOn,
  /**
   * Toggle variant
   */
  variant = 'primary',
  /**
   * Toggle onChange callback
   */
  onChange,
  /**
   * Toggle size, default is medium
   */
  size = 'medium',
  /**
   * Toggle text, default is on/off
   */
  text = {
    on: 'On',
    off: 'Off',
  },
}: ToggleProps) => {
  const toggleStateVariant: ToggleStateVariant = `${variant}-${
    isDisabled ? 'disabled' : 'active'
  }-${isOn ? 'on' : 'off'}`;

  const theme = useTheme() as Theme;

  const switcherVariants = useMemo(() => {
    const {
      sizes,
      components: {
        Toggle: {
          componentVariants: { size: sizeVariant, switcher },
        },
      },
    } = theme;
    const trackWidth = sizes[sizeVariant.variants[size].width as number];
    const switcherWidth =
      sizes[switcher.variants[toggleStateVariant].width as number];
    return {
      on: { x: trackWidth - switcherWidth },
      off: { x: 0 },
    };
  }, [size, theme]);

  return (
    <>
      <ToggleWrapper
        isDisabled={isDisabled}
        onClick={() => {
          if (!onChange || isDisabled) return;
          onChange(!isOn);
        }}
        onKeyDown={(e) => {
          if (!onChange || isDisabled) return;
          if (e.key === KEYBOARD_KEYS.ENTER && !isDisabled) onChange(!isOn);
        }}
        data-testid="toggle"
        role="switch"
        aria-checked={isOn}
        tabIndex={0}
      >
        <Track
          baseColor={`${variant}-${isDisabled ? 'disabled' : 'active'}`}
          size={size}
        >
          <TrackMotion
            colorVariant={toggleStateVariant}
            variants={trackVariants}
            initial={isOn ? 'on' : 'off'}
            animate={isOn ? 'on' : 'off'}
            transition={switcherTransition}
          />
        </Track>
        <LabelMotion
          textVariant={toggleStateVariant}
          size={size}
          variants={labelVariants}
          initial={isOn ? 'on' : 'off'}
          animate={isOn ? 'on' : 'off'}
          transition={labelTransition}
        >
          {isOn ? text.on : text.off}
        </LabelMotion>
        <SwitcherWrapper size={size}>
          <SwitcherMotion
            toggleState={toggleStateVariant}
            transition={switcherTransition}
            variants={switcherVariants}
            initial={isOn ? 'on' : 'off'}
            animate={isOn ? 'on' : 'off'}
          />
        </SwitcherWrapper>
      </ToggleWrapper>
    </>
  );
};

export default Toggle;
